import { Prompt } from "common/models/types";
import axiosInstance from "./axiosInstance";
import { ApiResponse } from "./drugService";
import { log, LogLevel } from "utils/LogUtil";

// 오프셋 기반 페이지네이션 파라미터 정의
export interface OffsetParams {
  offset?: number;
  limit?: number;
  search?: string;
  promptType?: string; // 프롬프트 타입 필터링 파라미터 추가
  orderBy?: string; // 정렬 기준 컬럼 (백엔드 기본: id)
  orderDirection?: "ASC" | "DESC"; // 정렬 방향 (백엔드 enum: 대문자)
}

/**
 * 프롬프트 목록 가져오기 (오프셋 방식 적용)
 * @param params 오프셋 파라미터 (offset, limit, search, promptType)
 * @returns 무한 스크롤을 위한 프롬프트 목록 결과
 */
export const fetchPrompts = async (
  params: OffsetParams = { offset: 0, limit: 20 }
): Promise<ApiResponse<Prompt[]>> => {
  try {
    // 빈 문자열, undefined, null 파라미터 제거
    const cleanParams: Record<string, any> = {};

    // 유효한 파라미터만 복사
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== "") {
        cleanParams[key] = value;
      }
    });

    log(LogLevel.VERBOSE, "fetchPrompts 깨끗한 파라미터:", JSON.stringify(cleanParams));

    // 실제 API 호출
    const response = await axiosInstance.get<ApiResponse<Prompt[]>>("/api/v1/prompts", {
      params: cleanParams,
    });
    return response.data;
  } catch (error) {
    log(LogLevel.ERROR, "프롬프트 목록 가져오기 실패:", error);
    throw error;
  }
};

/**
 * 프롬프트 상세 정보 가져오기
 * @param id 프롬프트 ID
 * @returns 프롬프트 상세 정보
 */
export const fetchPromptById = async (id: number): Promise<Prompt> => {
  try {
    const response = await axiosInstance.get<ApiResponse<Prompt>>(`/api/v1/prompts/${id}`);
    if (!response.data.data) {
      throw new Error("프롬프트 정보가 없습니다.");
    }
    return response.data.data;
  } catch (error) {
    log(LogLevel.ERROR, `프롬프트 상세 정보 가져오기 실패 (ID: ${id}):`, error);
    throw error;
  }
};

/**
 * 프롬프트 저장하기 (새로 만들기 또는 업데이트)
 * @param prompt 저장할 프롬프트 데이터
 * @returns 저장된 프롬프트 정보
 */
export const savePrompt = async (prompt: Prompt): Promise<Prompt> => {
  try {
    let response;

    if (prompt.id && prompt.id !== 0) {
      // 기존 프롬프트 업데이트 (PATCH 요청)
      response = await axiosInstance.patch<ApiResponse<Prompt>>(
        `/api/v1/prompts/${prompt.id}`,
        prompt
      );

      // error가 0이면 성공 응답으로 처리하고 원본 객체 반환
      if (response.data.error === 0) {
        // 서버에서 데이터를 반환했다면 그 데이터를 사용, 아니면 원본 객체 사용
        return response.data.data || prompt;
      }
    } else {
      // 새 프롬프트 생성 (POST 요청)
      // id가 0인 경우는 서버에 보내지 않도록 객체에서 제외
      const { id, ...promptWithoutId } = prompt;
      response = await axiosInstance.post<ApiResponse<Prompt>>("/api/v1/prompts", promptWithoutId);

      // error가 0이면 성공으로 처리
      if (response.data.error === 0) {
        // 서버에서 데이터가 없으면 원본 데이터 반환
        if (!response.data.data) {
          return { ...prompt, id: undefined }; // 새 id 없이 반환
        }

        // 서버로부터 받은 응답 데이터
        const savedPrompt = response.data.data;

        // 새 프롬프트 생성 시 서버가 반환한 id만 있을 경우 원본 prompt 객체와 합쳐서 반환
        if (
          typeof savedPrompt === "object" &&
          "id" in savedPrompt &&
          Object.keys(savedPrompt).length === 1
        ) {
          return {
            ...prompt,
            id: savedPrompt.id,
          };
        }

        // 서버에서 완전한 객체를 반환한 경우 그대로 사용
        return savedPrompt;
      }
    }

    // error가 0이 아니면 실패로 처리
    throw new Error(response.data.message || "프롬프트 저장에 실패했습니다.");
  } catch (error) {
    log(LogLevel.ERROR, "프롬프트 저장 실패:", error);
    throw error;
  }
};

/**
 * 프롬프트 삭제하기
 * @param id 삭제할 프롬프트 ID
 * @returns 삭제 성공 여부
 */
export const deletePrompt = async (id: number): Promise<boolean> => {
  try {
    const response = await axiosInstance.delete<ApiResponse<boolean>>(`/api/v1/prompts/${id}`);
    return response.data.error === 0;
  } catch (error) {
    log(LogLevel.ERROR, `프롬프트 삭제 실패 (ID: ${id}):`, error);
    throw error;
  }
};

/**
 * 프롬프트 활성화/비활성화 토글
 * @param id 토글할 프롬프트 ID
 * @param isActive 활성화 상태
 * @returns 업데이트된 프롬프트 정보
 */
export const togglePromptActive = async (id: number, isActive: boolean): Promise<Prompt> => {
  try {
    const response = await axiosInstance.put<ApiResponse<Prompt>>(
      `/api/v1/prompts/${id}/activate`,
      { isActive }
    );

    if (!response.data.data) {
      // 거절 봉투 { error: 5, message, data: undefined }가 오면 서버 message를 그대로 던진다
      // (저장 경로 savePrompt:121과 같게). dose 타입 활성화 거절 message를 토스트로 보이기 위함.
      throw new Error(response.data.message || "프롬프트 상태 변경에 실패했습니다.");
    }

    return response.data.data;
  } catch (error) {
    log(LogLevel.ERROR, `프롬프트 상태 변경 실패 (ID: ${id}):`, error);
    throw error;
  }
};

/**
 * 타입별 활성 프롬프트 조회 (GET /prompts/active/:promptType)
 * @param promptType 조회할 프롬프트 타입
 * @returns 활성 프롬프트. 활성이 없으면 서버가 { error: 0, data: null }을 주므로 null을 정상 반환.
 *          (편집기 타입 전환 시 모델 칸 채움 — D-6)
 */
export const fetchActivePrompt = async (promptType: string): Promise<Prompt | null> => {
  try {
    const response = await axiosInstance.get<ApiResponse<Prompt | null>>(
      `/api/v1/prompts/active/${encodeURIComponent(promptType)}`
    );
    // 활성 없음은 오류가 아니다 — null을 정상 응답으로 돌려준다(throw 금지).
    return response.data.data ?? null;
  } catch (error) {
    log(LogLevel.ERROR, `활성 프롬프트 조회 실패 (type: ${promptType}):`, error);
    throw error;
  }
};

// promptService 객체로 export
export const promptService = {
  getPrompts: fetchPrompts,
  getPromptById: fetchPromptById,
  savePrompt,
  deletePrompt,
  togglePromptActive,
  fetchActivePrompt,
};
